import cx from 'classnames';
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Model3DLoader from '../../components/Model3DLoader';
import { Loader } from '../../common';
import { getImageSrc } from '../NewProductUI/ProductDetails';
import styles from './styles.module.scss';
import { VideoSectionUIviaType } from '../NewProductUI/VideoSectionUI';
import React360 from '../../common/React360';
import { AnimatedLoader, checkStatus, getLoaded360Images } from './ProductDetailsV2';
import { ThreeSixtyIcon } from '../../assets/icons';
import { getProductPageType, wait } from '../../helpers/jsHelper';
import ArrowDownIconV2 from '../../components/icons/ArrowDownIconV2';
import { debounce } from 'lodash';
import useIsMobile from '../../hooks/useIsMobile';
import ARQRcode from '../../common/ARViewer/ARQRcode';
import useARViewerUrlParams from '../../hooks/useARViewerUrlParams';
import { dispatcher } from '../../helpers/projectHelper';
import { measureToolActions, selectIsMeasureTool } from '../../redux/slicers/measureToolSlicer';
import { useDispatch, useSelector } from 'react-redux';
import {
  apiGetPublicImageMeasurementData,
  apiGetPublicImageMeasurementDataShare,
} from '../../helpers/api';
import { ZoomInImage } from '../../common/PreviewDisplay';
import * as SpriteSpin from 'spritespin';
import {
  selectActiveView,
  selectProductInfo,
  setTriggerProductContentUpdateForPresentationView,
} from '../../redux/slicers/productV2Slicer';
import useCategoryContentActions from './useCategoryContentActions';
import { ProgressBarLine } from '../../components/gamificationTooltip/gamificationTooltip';
import { Mixpanel, MxEvent } from '../../common/Mixpanel';
import { useUserContext } from '../../contexts/UserContext';
import { STATUS_NOT_DONE_KEY } from '../../constants/constants';
import useSendDimensionsToParent from '../../hooks/useSendDimensionsToParent';

const {
  THREEDVIEWER,
  PRODUCTIMAGE,
  THREESIXTY,
  DIMENSIONSHOT,
  ARVIEWER,
  VIDEO,
  CONFIGURATOR,
  GIF,
} = getProductPageType();

export const GeneratingLoadingUI = ({
  style = {},
  loading = true,
  hideLoader = false,
  hideLoadingText = false,
  loaderStyle = {},
  progressBarStyle = {},
  progressBarTextStyle = {},
  loadingText = 'Generating visuals. Please wait...',
  hideProgressBar = true,
  progress = -1,
  bgOpacity = 1,
  textOpacity = '0.5'
}) => {
  if (!loading) return null;
  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{
        width: '100%',
        height: '100%',
        top: '0',
        left: '0',
        background: `rgba(255, 255, 255, ${bgOpacity})`,
        position: 'absolute',
        ...style,
      }}>
      {!hideLoader ? (
        <AnimatedLoader
          style={{
            width: '64px',
            height: '64px',
            ...loaderStyle,
          }}
        />
      ) : null}
      {!hideLoadingText ? (
        <h2
          className="m-0"
          style={{
            color: '#141414',
            fontSize: '0.875rem',
            fontWeight: '500',
            fontFamily: 'PPNeueMontreal-Medium',
            opacity: textOpacity,
          }}>
          {loadingText}
        </h2>
      ) : null}

      {!hideProgressBar || progress !== -1 ? (
        <ProgressBarLine
          className="mx-auto mt-3"
          progress={progress}
          designV2={true}
          showText={true}
          style={progressBarStyle}
          textStyle={progressBarTextStyle}
        />
      ) : null}
    </div>
  );
};

export const Model3DViewer = memo(
  forwardRef(
    (
      {
        dimensions,
        setLoading,
        loading = false,
        list = [],
        selectedItem = null,
        isTexture = false,
        isFullScreen = false,
        show = true,
        autoRotate = false,
        showDimension = false,
      },
      ref
    ) => {
      const _ref = ref || useRef();
      const [mloading, setMLoading] = useState(false);

      const _loading = loading || mloading;

      const handleLoading = useCallback(
        (v) => {
          if (setLoading) {
            setLoading(v);
          } else {
            setMLoading(v);
          }
        },
        [setLoading, setMLoading]
      );
      if (!show || !selectedItem) return null;

      if (!_loading && checkStatus(selectedItem)) return <GeneratingLoadingUI />;

      return (
        <>
          <Loader isV2={true} hideLoadingText={true} loading={_loading} />
          <Model3DLoader
            url={selectedItem?.glb}
            extension="glb"
            setModelLoading={handleLoading}
            setError={() => {}}
            applyTexture={isTexture}
            selectedItem={selectedItem}
            autoRotate={autoRotate}
            showDimension={showDimension}
            dimensions={dimensions}
            style={
              isFullScreen
                ? {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }
                : {}
            }
            ref={_ref}
          />
        </>
      );
    }
  )
);

export const ARViewer = ({ list = [], selectedItem = null, isPublicPage = false }) => {
  const { t } = useTranslation('common');
  const urlParams = useARViewerUrlParams();

  if (!selectedItem) return null;

  if (checkStatus(selectedItem)) return <GeneratingLoadingUI />;

  // Use custom content from URL if provided, otherwise use translations
  const titleText = urlParams.title || t('experience_it_for_yourself');
  const descriptionText = urlParams.description || t('scan_qr_code_message');
  const qrCodeSize = urlParams.qrCodeSize;

  return (
    <section
      className="d-flex justify-content-center align-items-center w-100 h-100"
      style={
        selectedItem?.thumbnail
          ? {
              background: `linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${selectedItem?.thumbnail}) lightgray 50% / contain no-repeat`,
            }
          : {}
      }
      onClick={(e) => e?.stopPropagation()}
      aria-label={`AR Viewer for ${selectedItem?.name || 'product'}`}>
      <div className={styles.arViewer} role="region" aria-labelledby="ar-viewer-heading">
        <ARQRcode
          propId={selectedItem?.product}
          propName={selectedItem?.name}
          isPublicPage={isPublicPage}
          showLoading={true}
          customButtonText={urlParams.buttonText}
          customButtonStyle={urlParams.buttonStyle}
          qrCodeSize={qrCodeSize}
        />
        <h1
          id="ar-viewer-heading"
          className={`${urlParams.titleStyle?.fontFamily ? '' : styles.title} m-0`}
          style={urlParams.titleStyle || {}}>
          {titleText}
        </h1>
        <p className={urlParams.descriptionStyle?.fontFamily ? '' : styles.subTitle} style={urlParams.descriptionStyle || {}}>
          {descriptionText}
        </p>
      </div>
    </section>
  );
};

const ImageViewer = ({
  eventProperties = {},
  list = [],
  selectedItem = null,
  setSelectedItem,
  contentFullScreen = false,
}) => {
  const { currentUser } = useUserContext();
  const [pass, setPass] = useState({});
  const imageRef = useRef();
  const imageContainerRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dimensions, setDimensions] = useState({ width: '100%', height: '100%' });
  const [leftRightDimensions, setLeftRightDimensions] = useState({ width: '60%' });
  const isMobile = useIsMobile();

  useEffect(() => {
    const throttledRecalculateSize = debounce(calculateImageDimensions, 70);
    throttledRecalculateSize();
    window.addEventListener('resize', throttledRecalculateSize);
    return () => {
      window.removeEventListener('resize', throttledRecalculateSize);
    };
  }, [currentIndex, imageContainerRef?.current, isMobile, pass]);

  useEffect(() => {
    if (selectedItem) {
      const index = list.findIndex((val) => val?.id === selectedItem?.id);
      setCurrentIndex(index);
    }
  }, [list, selectedItem]);

  useEffect(() => {
    if (setSelectedItem) setSelectedItem?.(list[currentIndex]);
  }, [list, currentIndex, setSelectedItem]);

  const getPreviousIndex = () => {
    if (list.length === 1) return -1; // Only one content
    if (currentIndex === 0) return list.length - 1; // Loop back to last image
    return currentIndex - 1;
  };

  const getNextIndex = () => {
    if (list.length === 1) return -1; // Only one content
    if (currentIndex === list.length - 1) return 0; // Loop back to first image
    return currentIndex + 1;
  };

  const previousIndex = getPreviousIndex();
  const nextIndex = getNextIndex();

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % list.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? list.length - 1 : prev - 1));
  };

  useEffect(() => {
    list?.forEach((item) => {
      setPass((prev) => ({
        ...prev,
        [item.id]: {
          default: item?.render_image_details?.img_type || "1",
          current: item?.render_image_details?.img_type || "1",
        },
      }));
    });
  }, [list]);

  const getImageData = (url, cb) => {
    const img = new Image();
    img.onload = () => cb(img);
    img.onerror = (err) => cb(err);
    img.src = url;
  };

  const getInnerSize = (element) => {
    const cs = getComputedStyle(element);
    const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const borderX = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
    const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    const width =
      element.clientWidth - paddingX - borderX - (isMobile || list.length <= 1 ? 0 : 150);
    const height = element.clientHeight - paddingY - borderY;
    return { width, height };
  };

  const calculateAspectRatioFit = (srcWidth, srcHeight, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    const width = Math.round(srcWidth * ratio);
    const height = Math.round(srcHeight * ratio);
    return { width: Math.round(width), height: Math.round(height) };
  };

  const calculateImageDimensions = () => {
    if (!imageContainerRef?.current) return;
    if(!Object?.keys(pass)?.length) return 
    const imageUrl = getImageSrc(list[currentIndex], pass);
    const callback = (image) => {
      const { width: maxAvailableWidth, height: maxAvailableHeight } = getInnerSize(
        imageContainerRef?.current
      );
      const { width, height } = calculateAspectRatioFit(
        image.naturalWidth,
        image.naturalHeight,
        maxAvailableWidth,
        maxAvailableHeight
      );
      const leftRightAvailableWidth = Math.abs(imageContainerRef?.current?.clientWidth - (width || maxAvailableWidth));
      setLeftRightDimensions({ width: leftRightAvailableWidth });
      setDimensions({ width:width || maxAvailableWidth, height:height || maxAvailableHeight });
    };
    getImageData(imageUrl?.download_file_link || imageUrl?.render_image_details?.file  || imageUrl, callback);
  };

  const triggerPageViewedEvent = useCallback(
    debounce((e) => {
      const state = e.state;
      if (state.scale > state.previousScale) {
        Mixpanel.track(true, MxEvent.PRODUCT_IMAGE_VIEWED, eventProperties);
      }
    }, 500),
    []
  );

  if (!selectedItem) return null;

  if (checkStatus(selectedItem)) return <GeneratingLoadingUI />;

  return (
    <div className={styles.carouselContainer} ref={imageContainerRef}>
      {/* <Loader loading={loading} /> */}
      {list.length > 1 && (
        <div className={`${styles.leftIcon} ${styles.icon}`} onClick={handlePrevious}>
          <ArrowDownIconV2 />
        </div>
      )}
      {!isMobile && !contentFullScreen && (
        <div
          className={`${styles.carouselBackground} ${styles.carouselBackgroundPrev}`}
          style={{
            width: `calc(${leftRightDimensions.width}px/2)`,
          }}>
          <div
            className={`w-100 h-100 ${styles.carouselImageNextPrev}`}
            style={{
              backgroundImage:
                previousIndex >= 0
                  ? `url("${getImageSrc(list[previousIndex], pass)}")`
                  : 'rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
      <div className={styles.carouselMain} style={{ ...dimensions }}>
        {/* <img
          src={getImageSrc(list[currentIndex], pass)}
          className={cx(styles.carouselImage, contentFullScreen ? styles.contentFullScreen : '')}
          ref={imageRef}
          alt={'CURRENT' || selectedItem?.display_name}
          onLoad={() => setLoading(false)}
          loading="lazy"
        /> */}
        {/* // Replace the above code because we need zoom and drag functionality.*/}
        <ZoomInImage
          src={getImageSrc(list[currentIndex], pass)}
          id={currentIndex}
          currentCard={selectedItem}
          activeCameraId={selectedItem?.render_image}
          activeIndex={currentIndex}
          ref={imageRef}
          hideLoading={true}
          onZoomCallback={triggerPageViewedEvent}
          isPublicPage={!currentUser?.id}
          isProductPage={true}
        />
      </div>
      {!isMobile && !contentFullScreen && (
        <div
          className={`${styles.carouselBackground} ${styles.carouselBackgroundNext}`}
          style={{
            width: `calc(${leftRightDimensions.width}px/2)`,
          }}>
          <div
            className={`w-100 h-100 ${styles.carouselImageNextPrev}`}
            style={{
              backgroundImage:
                nextIndex >= 0
                  ? `url("${getImageSrc(list[nextIndex], pass)}")`
                  : 'rgba(0, 0, 0, 0.5)',
            }}
          />
        </div>
      )}
      {list.length > 1 && (
        <div className={`${styles.rightIcon} ${styles.icon}`} onClick={handleNext}>
          <ArrowDownIconV2 />
        </div>
      )}
      {/* We are not showing the image passes in the product viewer */}
      {/* {imageRef?.current?.getAttribute('src') ? (
        <div className={styles.imagePasses}>
          {getImageTypeFooterComponent(pass, setPass, [selectedItem], 0)}
        </div>
      ) : null} */}
    </div>
  );
};

export const VideoViewer = ({
  loading = false,
  list = [],
  selectedItem = null,
  isActive = true,
}) => {
  const src =
    selectedItem?.video_details?.file ||
    selectedItem?.panningvideo_details?.file ||
    selectedItem?.aivideo_details?.file;

  if (!selectedItem) return null;

  if (loading) return <Loader isV2={true} loading />;

  return (
    <VideoSectionUIviaType
      containerClassname="w-100 h-100"
      className={styles.videoPlayer}
      url={src}
      id={selectedItem?.id}
      autoPlay={isActive}
      playsInline={true}
    />
  );
};

const ConfiguratorViewer = ({ loading = false, list = [], selectedItem = null }) => {
  if (!selectedItem) return null;

  const url = selectedItem?.configurator_details?.unique_id
    ? `${window.location.origin}/configurator-viewer/${selectedItem?.configurator_details?.unique_id}#toolbar=0`
    : null;

  if (loading) return <Loader isV2={true} hideLoadingText={true} loading position="absolute" />;

  return url ? <iframe className="w-100 h-100" src={url}></iframe> : null;
};

export const Viewer360 = forwardRef(
  ({ productInfo, loading: _loading, list = [], selectedItem = null, onClose }, ref) => {
    const { t } = useTranslation('common');
    const [loading, setLoading] = useState(true);
    const [images, setImages] = useState([]);
    const status = checkStatus(selectedItem);

    const loadImages = async () => {
      setImages([]);
      await wait(500);
      setLoading(false);
      const _images = selectedItem?.multiviewergroup_details?.images;
      if (_images?.length > 23 && !status) {
        setLoading(true);
        setImages(await getLoaded360Images(_images));
        setLoading(false);
      }
    };

    useEffect(() => {
      if (selectedItem) loadImages();
    }, [selectedItem]);

    const __loading = _loading || loading;

    if (images?.length > 23 && !status && !__loading) {
      return (
        <div className="h-100 w-100 position-relative">
          <React360
            className="h-100"
            // imgContainerClassname="bg-transparent"
            numImages={images?.length}
            productImages={images?.map((i) => i?.image_file) || []}
            baseImages={images?.map((i) => i?.shadow_image_file)?.filter(Boolean) || []}
            setImageLoaded={() => setLoading(false)}
            imgContainerStyle={
              selectedItem?.multiviewergroup_details?.color
                ? {
                    background: selectedItem?.multiviewergroup_details?.color,
                  }
                : {}
            }
            imgStyle={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
            baseImgStyle={{
              opacity: 0.35,
            }}
            containerRef={ref}
            onClose={onClose}
            enableClick={true}
          />
          <span
            className={styles.rotateInfo}
            onClick={(e) => {
              e?.stopPropagation();
            }}>
            <ThreeSixtyIcon /> <span>{t('click_and_drag_to_rotate')}</span>
          </span>{' '}
        </div>
      );
    }

    return (
      <div className="position-relative w-100 h-100 d-flex align-items-center justify-content-center">
        <Loader loading={__loading} position="absolute" />
        <img
          src={
            selectedItem?.multiviewergroup_details?.images?.[0]?.thumbnail || productInfo?.thumbnail
          }
          alt=""
          className="invisible" // making it invisible to avoid showing the product image while loading as it making the user experience not good
          // onLoad={() => setLoading(false)}
        />
        {status && !__loading ? <GeneratingLoadingUI /> : null}
      </div>
    );
  }
);

export const DimensionShotImageViewer = memo(
  ({
    eventProperties = {},
    loading: _loading = false,
    list = [],
    selectedItem = null,
    setSelectedItem,
    isActive = true,
    isShowcaseViewer = false,
    viewerFullScreen = false,
    contentFullScreen = false,
    isPublicPage = false,
    setParentPass,
    callbackWhenImageLoaded: _callbackWhenImageLoaded,
    hideSidebar = false,
  }) => {
    const { currentUser } = useUserContext();
    const dispatch = useDispatch();
    const [pass, setPass] = useState({});
    const [loading, setLoading] = useState(true);
    const imageRef = useRef();
    const imageContainerRef = useRef();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [dimensions, setDimensions] = useState({ width: '100%', height: '100%' });
    const [leftRightDimensions, setLeftRightDimensions] = useState({ width: '60%' });
    const isMobile = useIsMobile();
    // dimension measurements - begins
    const [showMeasureTool, setShowMeasureTool] = useState(isActive);
    const isMeasureToolActive = useSelector(selectIsMeasureTool);
    const [scale, setScale] = useState(0.99);
    const controllerRef = useRef(new AbortController());
    const productInfo = useSelector(selectProductInfo);
    const debounceTimeoutRef = useRef(null);

    useEffect(() => {
      return () => controllerRef?.current?.abort();
    }, []);

    const { onDownloadAndUploadDimensionShot } = useCategoryContentActions({
      productInfo,
      category: getProductPageType().DIMENSIONSHOT,
    });

    /**
     * A callback function that is called when an image is loaded
     * and, if the file was uploaded successfully or not with key 'is_dimension_shot_file'
     * preventing the callback from calling in mobile in order to upload the smaller image
     */
    const callbackWhenImageLoaded = useCallback(
      (v) => {
        // Clear any existing timeout to debounce calls
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        // Set new debounce timeout
        debounceTimeoutRef.current = setTimeout(() => {
          if (_callbackWhenImageLoaded) {
            _callbackWhenImageLoaded(v);
          } else if (
            !selectedItem?.dimension_shot_image_details?.is_dimension_shot_file &&
            !isMobile
          ) {
            onDownloadAndUploadDimensionShot({
              target: '.lf-zoom-image-container',
              item: selectedItem,
              ref: controllerRef,
              callback: () => {
                setTimeout(() => {
                  dispatch(
                    setTriggerProductContentUpdateForPresentationView(
                      getProductPageType().DIMENSIONSHOT
                    )
                  );
                }, 500);
              },
            });
          }
        }, 5000); // delay of 5000 second
        // When the user updates images from one residential type to another, it sometimes causes issues due to API calls being triggered too quickly.
        // This results in the wrong image reference being used, which can cause incorrect dimension shots or positions not being set properly.
        // To prevent this, a 5-second delay has been added. 
        // Reducing the delay below 5 seconds still causes the issue, so please do not lower the timer.
      },
      [debounceTimeoutRef, _callbackWhenImageLoaded, selectedItem, onDownloadAndUploadDimensionShot]
    );

    const adjustScale = () => setScale((prevScale) => (prevScale === 1 ? 0.99 : 1));

    useEffect(() => {
      // Set the scale based on the current value
      setScale((prevScale) => (prevScale === 1 ? 0.99 : 1));

      // Attach the resize event listener
      window.addEventListener('resize', adjustScale);

      // Cleanup the event listener on component unmount
      return () => window.removeEventListener('resize', adjustScale);
    }, [viewerFullScreen, contentFullScreen]);

    useEffect(() => {
      dispatcher(measureToolActions.setIsStampingAllowed(false));
    }, []);

    useEffect(() => {
      const throttledRecalculateSize = debounce(calculateImageDimensions, 70);
      throttledRecalculateSize();
      window.addEventListener('resize', throttledRecalculateSize);
      return () => {
        window.removeEventListener('resize', throttledRecalculateSize);
      };
    }, [loading, currentIndex, imageContainerRef?.current, isMobile]);

    useEffect(() => {
      if (selectedItem) {
        const index = list.findIndex((val) => val?.id === selectedItem?.id);
        setCurrentIndex(index);
      }
    }, [list, selectedItem, isActive]);

    useEffect(() => {
      if (setSelectedItem) setSelectedItem?.(list[currentIndex]);
    }, [list, currentIndex, setSelectedItem]);

    useEffect(() => {
      list?.forEach((item) => {
        setPass((prev) => ({
          ...prev,
          [item.id]: {
            default: item?.render_image_details?.img_type || "1",
            current: item?.render_image_details?.img_type || "1",
          },
        }));
        setParentPass?.((prev) => ({
          ...prev,
          [item.id]: {
            default: item?.render_image_details?.img_type || "1",
            current: item?.render_image_details?.img_type || "1",
          },
        }));
      });
    }, [list]);

    useEffect(() => {
      if (selectedItem && showMeasureTool && isActive) {
        resetMeasurementLines();
        setLoading(_loading);
      }
      return () => resetMeasurementLines();
    }, [selectedItem, showMeasureTool, isActive, isShowcaseViewer, _loading]);

    const getPreviousIndex = () => {
      if (list.length === 1) return -1; // Only one content
      if (currentIndex === 0) return list.length - 1; // Loop back to last image
      return currentIndex - 1;
    };

    const getNextIndex = () => {
      if (list.length === 1) return -1; // Only one content
      if (currentIndex === list.length - 1) return 0; // Loop back to first image
      return currentIndex + 1;
    };

    const previousIndex = getPreviousIndex();
    const nextIndex = getNextIndex();

    const handleNext = () => {
      setCurrentIndex((prev) => (prev + 1) % list.length);
    };

    const handlePrevious = () => {
      setCurrentIndex((prev) => (prev === 0 ? list.length - 1 : prev - 1));
    };

    const getImageData = (url, cb) => {
      const img = new Image();
      img.onload = () => cb(img);
      img.onerror = (err) => cb(err);
      img.src = url;
    };

    const getInnerSize = (element) => {
      const cs = getComputedStyle(element);
      const paddingX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
      const paddingY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
      const borderX = parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
      const borderY = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
      const width =
        element.clientWidth - paddingX - borderX - (isMobile || list.length <= 1 ? 0 : 150);
      const height = element.clientHeight - paddingY - borderY;
      return { width, height };
    };

    const calculateAspectRatioFit = (srcWidth, srcHeight, maxWidth, maxHeight) => {
      const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
      const width = Math.round(srcWidth * ratio);
      const height = Math.round(srcHeight * ratio);
      return { width: Math.round(width), height: Math.round(height) };
    };

    const calculateImageDimensions = () => {
      if (!imageContainerRef?.current || loading || hideSidebar) return;
      const imageUrl = getImageSrc(list[currentIndex], pass);
      const callback = (image) => {
        const { width: maxAvailableWidth, height: maxAvailableHeight } = getInnerSize(
          imageContainerRef?.current
        );
        const { width, height } = calculateAspectRatioFit(
          image.naturalWidth,
          image.naturalHeight,
          maxAvailableWidth,
          maxAvailableHeight
        );
        const leftRightAvailableWidth = Math.abs(imageContainerRef?.current?.clientWidth - width);
        setLeftRightDimensions({ width: leftRightAvailableWidth });
        setDimensions({ width, height });
      };
      getImageData(imageUrl, callback);
      setTimeout(() => adjustScale(), 10);
    };

    const resetMeasurementLines = () => {
      dispatcher(measureToolActions.resetState());
      getImageData(getImageSrc(selectedItem, pass), () => {
        setTimeout(() => setLoading(false), 1000);
      });
    };

    const triggerPageViewedEvent = useCallback(
      debounce((e) => {
        const state = e.state;
        if (state.scale > state.previousScale) {
          Mixpanel.track(true, MxEvent.PRODUCT_IMAGE_VIEWED, eventProperties);
        }
      }, 500),
      []
    );

    if (!selectedItem) return null;

    if (checkStatus(selectedItem)) return <GeneratingLoadingUI />;

    return (
      <div
        className={cx(
          styles.carouselContainer,
          isShowcaseViewer ? 'bg-transparent' : '',
          isMobile ? 'bg-white' : ''
        )}
        ref={imageContainerRef}>
        <Loader loading={loading} style={loading ? { background: '#fff' } : {}} />
        {list.length > 1 && (
          <div className={`${styles.leftIcon} ${styles.icon}`} onClick={handlePrevious}>
            <ArrowDownIconV2 />
          </div>
        )}
        {!isMobile && !contentFullScreen && !hideSidebar && (
          <div
            className={`${styles.carouselBackground} ${styles.carouselBackgroundPrev}`}
            style={{
              width: `calc(${leftRightDimensions.width}px/2)`,
            }}>
            <div
              className={`w-100 h-100 ${styles.carouselImageNextPrev}`}
              style={{
                backgroundImage:
                  previousIndex >= 0
                    ? `url("${getImageSrc(list[previousIndex], pass)}")`
                    : 'rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        )}
        <div className={styles.carouselMain} style={{ ...dimensions }}>
          {selectedItem?.render_image &&
          !selectedItem?.dimension_shot_image_details?.is_dimension_shot_file &&
          isActive ? (
            <ZoomInImage
              src={getImageSrc(selectedItem, pass)}
              id={currentIndex}
              isMeasureToolActive={true || isMeasureToolActive}
              currentCard={selectedItem}
              activeCameraId={selectedItem?.render_image}
              downloadLoading={false} // downloadLoading}
              isMeasureToolEditable={false} // isMeasureToolEditable}
              showMeasureTool={showMeasureTool}
              toggleOffMeasureTool={() => setShowMeasureTool(!showMeasureTool)}
              loading={loading}
              activeIndex={currentIndex}
              ref={imageRef}
              apiGetMeasurementData={
                isPublicPage
                  ? apiGetPublicImageMeasurementDataShare
                  : apiGetPublicImageMeasurementData
              }
              scale={scale}
              isFullscreen={true}
              disableTransform={true}
              callbackWhenImageLoaded={callbackWhenImageLoaded}
              isActiveId={selectedItem?.render_image}
              isProductPage={true}
            />
          ) : (
            // <img
            //   key={selectedItem?.id}
            //   src={
            //     selectedItem?.dimension_shot_image_details?.dimension_shot_file ||
            //     getImageSrc(list[currentIndex], pass)
            //   }
            //   className={cx(
            //     styles.carouselImage,
            //     contentFullScreen ? styles.contentFullScreen : ''
            //   )}
            //   ref={imageRef}
            //   alt={'CURRENT' || selectedItem?.display_name}
            //   loading="lazy"
            //   onLoad={() => setLoading(false)}
            // />
            // Replace the above code because we need zoom and drag functionality.
            <ZoomInImage
              src={
                selectedItem?.dimension_shot_image_details?.dimension_shot_file ||
                getImageSrc(list[currentIndex], pass)
              }
              id={currentIndex}
              currentCard={selectedItem}
              activeCameraId={selectedItem?.render_image}
              activeIndex={currentIndex}
              ref={imageRef}
              hideLoading={true}
              onZoomCallback={triggerPageViewedEvent}
              isPublicPage={!currentUser?.id}
              apiGetMeasurementData={
                isPublicPage
                  ? apiGetPublicImageMeasurementDataShare
                  : apiGetPublicImageMeasurementData
              }
              isProductPage={true}
            />
          )}
          {/* {showMeasureTool && !loading && (
          <MeasureToolContainer
            allStyleSectionData={allStyleSectionData}
            isMeasureToolEditable={true} //currentOrg === selectedItem?.owner_organization}
            isMeasureToolActive={isMeasureToolActive}
            activeCameraId={list?.[currentIndex]?.id}
            lineValues={lineValues}
            currentCard={list[currentIndex]}
            toggleOffMeasureTool={(val) => setShowMeasureTool(val)}
            setIsMeasureToolActive={(val) => setIsMeasureToolActive(val)}
            isFullScreen={false}
          />
        )} */}
        </div>
        {!isMobile && !contentFullScreen && !hideSidebar && (
          <div
            className={`${styles.carouselBackground} ${styles.carouselBackgroundNext}`}
            style={{
              width: `calc(${leftRightDimensions.width}px/2)`,
            }}>
            <div
              className={`w-100 h-100 ${styles.carouselImageNextPrev}`}
              style={{
                backgroundImage:
                  nextIndex >= 0
                    ? `url("${getImageSrc(list[nextIndex], pass)}")`
                    : 'rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        )}
        {list.length > 1 && (
          <div className={`${styles.rightIcon} ${styles.icon}`} onClick={handleNext}>
            <ArrowDownIconV2 />
          </div>
        )}
        {/* We are not showing the image passes in the product viewer */}
        {/* {imageRef?.current?.getAttribute('src') ? (
        <div className={styles.imagePasses}>
          {getImageTypeFooterComponent(pass, setPass, [selectedItem], 0)}
        </div>
      ) : null} */}
      </div>
    );
  }
);

export const Viewer360V2 = (props) => {
  if(props?.productInfo?.generate_360_content_status === STATUS_NOT_DONE_KEY){
    return null
  }
  return <Viewer360V2UI {...props}/>
}

export const Viewer360V2UI = memo(
  forwardRef(
    (
      {
        category,
        productInfo,
        loading: _loading,
        list = [],
        selectedItem = null,
        onClose,
        apiProps = null,
        show = true,
        urlParams = {},
      },
      ref
    ) => {
      const { t } = useTranslation('common');
      const dispatch = useDispatch();
      const abortController = new AbortController(); // Create an instance of AbortController
      const _ref = ref || useRef();
      const spriteSpinViewerRef = useRef(null);
      const [loading, setLoading] = useState({ assets: true, viewer: true });
      const [currentFrame, setCurrentFrame] = useState(0);
      const [frameTime, setFrameTime] = useState(0);
      const [images, setImages] = useState([]);
      const [shadowImages, setShadowImages] = useState([]);
      const [progress, setProgress] = useState(-1);
      const status = checkStatus(selectedItem);
      const frameTimeout = 166.66;
      const __loading = _loading || loading.assets || loading.viewer;
      const contentRef = useRef(null);
      // Send dimensions to parent iframe on mount and resize
      useSendDimensionsToParent({ isReady: !status && !__loading && show, contentRef });

      useEffect(() => {
        if(frameTime === 0 && !loading && images?.length){
           setFrameTime(60)
        }
      }, [loading]);

      const updateProgress = (item) => {
        if (!item) return setProgress(-1);
        const count = item?.multiviewergroup_details?.images?.length || 0;
        const p = Math.ceil((count / 24) * 100);
        if (p >= 100) dispatch(setTriggerProductContentUpdateForPresentationView(category));
        else setProgress(p < 2 ? 2 : p);
      };

      const checkIsReadyToLoad = async () => {
        const response = await apiProps?.api({ id: productInfo?.id, contentId: selectedItem?.id });
        if (response) updateProgress(response);
      };

      const create360 = (m = []) => {
        const spriteSpinObj = {
          target: _ref?.current,
          height: '100%',
          width: '100%',
          source: Object.values(m),
          sourceZoom: [],
          sense: 2,
          animate: false,
          frame: frameTime || 0,
          zoomUseClick: true,
          zoomUseWheel: true,
          zoomPinFrame: true,
          renderMode: 'background',
          plugins: ['360', 'drag', 'progress'],
          responsive: true,
          frameTime: frameTimeout,
          onFrameChanged: function (e, data) {
            setCurrentFrame(data.frame);
          },
          onComplete: () => {
            setLoading((p) => ({ ...p, viewer: false }));
          },
        };
        spriteSpinViewerRef.current = SpriteSpin.spritespin(spriteSpinObj);
      };

      const loadImages = async () => {
        setImages([]);
        setShadowImages([]);
        setCurrentFrame(0);
        if (spriteSpinViewerRef?.current) {
          spriteSpinViewerRef.current.target = null;
        }
        const _images = selectedItem?.multiviewergroup_details?.images;
        if (_images?.length > 23 && !status) {
          setLoading((p) => ({ ...p, viewer: true, assets: true }));
          const m = await getLoaded360Images(_images, false);
          const imagesFile = m?.map((a) => a?.image_file);

          const imagesShadow = m?.map((a) => a?.shadow_image_file);
          if (imagesShadow.length > 0) {
            setShadowImages(
              (
                await Promise.all(
                  imagesShadow?.map(async (a) => await fetch(a).then((r) => r.blob()))
                )
              ).map((b) => URL.createObjectURL(b))
            );
          }
          setImages(imagesFile);
          setLoading((p) => ({ ...p, assets: false }));
        } else {
          setLoading((p) => ({ ...p, assets: false }));
        }
      };

      useEffect(() => {
        let timerId;
        if (selectedItem) {
          if (selectedItem?.multiviewergroup_details?.images?.length < 24) {
            updateProgress(selectedItem);
            timerId = setInterval(() => checkIsReadyToLoad?.(), 5000);
          } else {
            loadImages();
            if (timerId) clearInterval(timerId);
          }
        }
        return () => {
          if (timerId) clearInterval(timerId);
        };
      }, [selectedItem]);

      useEffect(() => {
        let timerId;
        if (
          !_loading &&
          (!selectedItem || selectedItem?.multiviewergroup_details?.images?.length < 23)
        ) {
          timerId = setTimeout(() => setLoading({ assets: false, viewer: false }), 500);
        }
        return () => {
          if (timerId) clearTimeout(timerId);
        };
      }, [_loading, selectedItem]);

      const preloadImages = (urls) => {
        return Promise.all(
            urls.map((url) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = url;
                    img.onload = () => resolve(url);
                    img.onerror = reject;
                });
            })
        );
    };

      const createBlobURL = async (e) => {
        const blobs = await Promise.all(
          e?.map((url) =>
            fetch(url, { signal: abortController.signal }).then((response) => response.blob())
          )
        );
        const blobUrls = blobs.map((blob) => URL.createObjectURL(blob));
        // Preload images to avoid flickering
        await preloadImages(blobUrls);
        create360(blobUrls);
      };

      useEffect(() => {
        if (_ref.current && images?.length){
          createBlobURL(images)
        }
      }, [_ref.current, images, frameTime]);

      // if (status && !__loading) return <GeneratingLoadingUI />;

      if (!show) return null;

      return (
        <section
          className="w-100 h-100"
          style={
            selectedItem?.multiviewergroup_details?.color
              ? {
                  background: selectedItem?.multiviewergroup_details?.color,
                }
              : {}
          }
          aria-label="360 degree product viewer"
          ref={contentRef}
          >
          <h1 className="visually-hidden">360 Degree Product Viewer</h1>
          <Loader
            isV2={true}
            loading={status || __loading}
            hideLoadingText={!status}
            hideProgressBar={progress === -1 || !status}
            style={{ zIndex: 10 }}
            progress={progress}
          />
          <div
            ref={_ref}
            style={{ zIndex: 2, opacity: __loading ? 0 : 1 }}
            role="application"
            aria-label="Interactive 360 degree view - click and drag to rotate"></div>
          {images?.length > 23 && shadowImages?.length > 23 && !__loading && (
            <>
              <div
                className="position-absolute w-100 h-100"
                style={{
                  top: 0,
                  left: 0,
                  zIndex: 1,
                  opacity: __loading ? 0 : 0.35,
                }}
                aria-hidden="true">
                {shadowImages?.map((a, i) => (
                  <img
                    key={i}
                    src={a}
                    alt=""
                    className={currentFrame === i ? 'd-block' : 'd-none'}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                ))}
              </div>
              {!status && !__loading && (
                <p
                  className={`${styles.rotateInfo} m-0`}
                  onClick={(e) => {
                    e?.stopPropagation();
                  }}>
                  <ThreeSixtyIcon
                    style={{
                      ...(urlParams?.helpTextStyle?.fontSize
                        ? {
                            width: urlParams?.helpTextStyle?.fontSize,
                            height: urlParams?.helpTextStyle?.fontSize,
                          }
                        : {}),
                    }}
                    {...(urlParams?.helpTextStyle?.color
                      ? { color: urlParams?.helpTextStyle?.color }
                      : {})}
                    aria-hidden="true"
                  />{' '}
                  <span style={{ lineHeight: 1, ...(urlParams?.helpTextStyle || {}) }}>
                    {typeof urlParams?.helpText === 'string'
                      ? urlParams?.helpText
                      : t('click_and_drag_to_rotate')}
                  </span>
                </p>
              )}
            </>
          )}
        </section>
      );
    }
  )
);

export const PRODUCT_COMPONENT_MAPPING = {
  [THREEDVIEWER]: Model3DViewer,
  [THREESIXTY]: Viewer360V2,
  [ARVIEWER]: ARViewer,
  [PRODUCTIMAGE]: ImageViewer,
  [DIMENSIONSHOT]: DimensionShotImageViewer,
  [GIF]: ImageViewer,
  [VIDEO]: VideoViewer,
  [CONFIGURATOR]: ConfiguratorViewer,
};
